<template>
  <div class="cropper-container">
    <input
      :id="inputId"
      ref="input"
      type="file"
      class="d-none"
      multiple
      @change="setImage"
    />
    <div class="upload">
      <div class="upload__wrapper">
        <label class="upload__image-label" :for="inputId">
          <template v-if="!imageUrl">
            <template v-if="!imageLoader">
              <v-icon size="48" color="primary">icon-photo-add</v-icon>

              <ui-button
                color="primary"
                class="btn--add-photo"
                @click="handleChangeImgBtn"
                >{{ $t("message.photos.addPhoto") }}</ui-button
              >
              <p v-if="headerTooltip" class="upload__requirements">
                {{ $t(headerTooltip) }}
              </p>
            </template>
          </template>
          <img
            v-else
            @click="handleImageEdit"
            :src="imageUrl"
            :alt="$t('message.common.previewPhoto')"
          />
          <SectionLoader :status="imageLoader" :opacity="0.75" />
        </label>
        <v-btn
          v-if="imageUrl && image"
          icon
          @click="handleImageEdit"
          class="upload__edit-image"
        >
          <v-icon>
            icon-edit
          </v-icon>
        </v-btn>
        <ui-button
          v-if="imageUrl && !image"
          color="primary"
          @click="handleChangeImgBtn"
          class="upload__edit-image upload__edit-image--no-image"
        >
          {{ $t("message.photos.changePhoto") }}
        </ui-button>
      </div>
      <v-dialog
        max-width="630"
        v-if="showDialog && image"
        v-model="isOpen"
        persistent
        content-class="custom-dialog"
      >
        <v-card class="pa-4">
          <div class="top-actions">
            <div class="cropper__actions">
              <ui-button icon="icon-rotate-left" @click.prevent="rotate(-90)">{{
                $t("message.photos.edit.rotateLeft")
              }}</ui-button>
              <ui-button icon="icon-rotate-right" @click.prevent="rotate(90)">{{
                $t("message.photos.edit.rotateRight")
              }}</ui-button>
              <ui-button color="pink" outlined @click.prevent="reset">{{
                $t("message.photos.edit.reset")
              }}</ui-button>
            </div>
            <v-btn icon @click="showDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
          <InfoBox
            class="top-actions__info-box"
            :text="$t('message.photos.edit.infoBox')"
          />
          <div class="cropper">
            <vue-cropper
              :key="cropperId"
              class="cropper__editor"
              ref="cropper"
              :guides="true"
              drag-mode="crop"
              :auto-crop-area="0.75"
              :background="true"
              :src="imgSrc"
            ></vue-cropper>
          </div>
          <div class="bottom-actions">
            <ui-button color="primary" outlined @click="handleChangeImgBtn">
              {{ $t("message.photos.changePhoto") }}
            </ui-button>
            <ui-button color="primary" @click="cropImage">
              {{ $t("message.photos.edit.applyChanges") }}
            </ui-button>
          </div>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script>
import VueCropper from "vue-cropperjs";
import "cropperjs/dist/cropper.css";
import SectionLoader from "@/components/Loader/SectionLoader.vue";
import InfoBox from "@/components/ui/InfoBox.vue";

import cuid from "cuid";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";

export default {
  name: "AddPhoto",
  data() {
    return {
      inputId: null,
      cropperId: null,
      image: null,
      cropImg: null,
      imgSrc: "",
      showDialog: false,
      isOpen: true
    };
  },

  props: {
    imageLoader: {
      type: Boolean,
      required: true
    },
    imageUrl: {
      type: String,
      required: false
    },
    headerTooltip: {
      type: String,
      default: null
    },
    file: {
      type: File,
      default: null
    }
  },

  components: {
    VueCropper,
    SectionLoader,
    InfoBox
  },

  created() {
    this.inputId = cuid();
    if (this.file) {
      this.image = this.file;
      this.createImageReader(this.image);
    }
  },

  beforeDestroy() {
    this.inputId = null;
  },

  watch: {
    imageUrl(newVal) {
      if (newVal === null) {
        this.resetImage();
      }
    }
  },

  methods: {
    handleDeleteImg() {
      this.$emit("delete");
    },
    handleChangeImgBtn() {
      document.getElementById(this.inputId).click();
    },
    uploadImage({ file, edited }) {
      if (file) {
        this.$emit("upload-image", { file, edited });
      }
    },
    createImageReader(image) {
      if (typeof FileReader === "function") {
        const reader = new FileReader();
        reader.onload = event => {
          this.imgSrc = event.target.result;
          this.cropperId = cuid();
        };
        reader.readAsDataURL(image);
      } else {
        alert("Sorry, FileReader API not supported");
      }
    },
    setImage(e) {
      if (!e.target.files.length) {
        return;
      }
      this.image = e.target.files[0];
      if (e.target.files.length > 1) {
        const moreImages = Array.from(e.target.files).slice(1);
        this.$emit("upload-many-images", moreImages);
      }
      this.uploadImage({ file: this.image, edited: false });

      if (!this.image.type.includes("image/")) {
        return;
      }
      this.createImageReader(this.image);
    },
    cropImage() {
      // get image data for post processing, e.g. upload or setting image src
      this.cropImg = this.$refs.cropper
        .getCroppedCanvas()
        .toDataURL(this.image.type, 0.9);

      const file = this.dataURLtoFile(
        this.cropImg,
        this.image ? this.image.name : "image.png"
      );

      this.uploadImage({ file, edited: true });

      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.PHOTO_EDIT
      });
    },
    rotate(deg) {
      this.$refs.cropper.rotate(deg);
    },
    reset() {
      this.$refs.cropper.reset();
    },
    dataURLtoFile(dataurl, filename) {
      var arr = dataurl.split(","),
        mime = arr[0].match(/:(.*?);/)[1],
        bstr = atob(arr[1]),
        n = bstr.length,
        u8arr = new Uint8Array(n);

      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }

      return new File([u8arr], filename, { type: mime });
    },
    resetImage() {
      this.imgSrc = "";
      this.image = null;
      this.cropImg = null;
      this.$refs.input.value = "";
    },
    handleImageEdit(event) {
      event.preventDefault();
      this.showDialog = true;
    }
  }
};
</script>

<style lang="scss" scoped>
.cropper-container {
  width: 100%;
}

.upload {
  position: relative;
  display: flex;
  height: 100%;

  &__wrapper {
    width: 100%;

    img {
      height: 100%;
      object-fit: cover;
    }
  }

  &__image-label {
    cursor: pointer;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 100%;
    color: $primary;
    position: relative;
    border-radius: 16px;
    font-weight: bold;
    overflow: hidden;
    background-color: $primary-light;
    box-shadow: 0 0 10px $primary-light;

    i {
      margin-bottom: $spacing * 3;
    }
  }

  label {
    cursor: pointer;
  }

  &__requirements {
    @include font-size(12px);
    line-height: 1.2;
    text-align: center;
    margin: $spacing * 3 $spacing * 4 0;
    font-weight: 400;
    color: rgba($dark-blue-3, 0.6);
  }

  &__edit-image {
    position: absolute;
    top: $spacing;
    right: $spacing;
    background-color: $light;
    color: $primary;
    border-radius: 50%;

    &--no-image {
      border-radius: 40px;
    }
  }
}

.progress-loader {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cropper {
  width: 100%;

  &__editor {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    color: $primary;
    width: 100%;
    height: 100%;
    min-height: 200px;
    border-radius: 20px;
    overflow: hidden;
    margin-bottom: $spacing * 2;
  }

  &__loader {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    min-height: 250px;
  }

  &__actions {
    display: flex;
    justify-content: space-evenly;

    button:not(:last-child) {
      margin-right: $spacing;
    }
  }
}

.top-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: $spacing * 2;

  &__info-box {
    width: 100%;
    margin-bottom: $spacing * 2;
  }
}

.bottom-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: $spacing * 2;
}
</style>
